import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, Grid, FormControl, InputLabel,
  Select, MenuItem, Switch, FormControlLabel, Alert, Divider, CircularProgress,
  IconButton, ImageList, ImageListItem, ImageListItemBar, LinearProgress, Tabs, Tab,
  Accordion, AccordionSummary, AccordionDetails, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Checkbox, Stack, Autocomplete,
} from '@mui/material';
import {
  Save, ArrowBack, CloudUpload, Delete, AddPhotoAlternate, MyLocation, Add,
  MeetingRoom, CheckCircle, ExpandMore,
} from '@mui/icons-material';
import { propertiesAPI, ghanaAPI } from '../../api/client';
import { integerOnly, decimalOnly } from '../../utils/inputHelpers';

function EditPropertyPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [universities, setUniversities] = useState([]);

  const fetchCities = async (region) => {
    try {
      const res = await ghanaAPI.getCities({ region });
      setCities(res.data.cities || []);
    } catch (_) {}
  };

  useEffect(() => {
    const fetchGhanaData = async () => {
      try {
        const [regRes, uniRes] = await Promise.all([
          ghanaAPI.getRegions(),
          ghanaAPI.getUniversities(),
        ]);
        setRegions(regRes.data.regions || []);
        setUniversities((uniRes.data.universities || []).map(u => `${u.name} (${u.abbreviation})`));
      } catch (_) {}
    };
    fetchGhanaData();
  }, []);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const res = await propertiesAPI.getById(id);
        const p = res.data;
        setForm({
          title: p.title || '', description: p.description || '',
          property_type: p.property_type, location_type: p.location_type,
          address: p.address || '', city: p.city || '', region: p.region || '',
          nearby_university: p.nearby_university || '', distance_to_campus: p.distance_to_campus || '',
          latitude: p.latitude || '', longitude: p.longitude || '',
          price_per_month: p.price_per_month || '', price_per_semester: p.price_per_semester || '',
          price_per_year: p.price_per_year || '', deposit_amount: p.deposit_amount || '',
          total_rooms: p.total_rooms, available_rooms: p.available_rooms,
          room_type: p.room_type, max_occupants_per_room: p.max_occupants_per_room,
          allows_pairing: p.allows_pairing, has_electricity: p.has_electricity,
          has_water: p.has_water, has_wifi: p.has_wifi, has_security: p.has_security,
          has_parking: p.has_parking, has_laundry: p.has_laundry, has_kitchen: p.has_kitchen,
          has_furnished: p.has_furnished, has_ac: p.has_ac, has_generator: p.has_generator,
          rules: p.rules || '', allows_pets: p.allows_pets, allows_smoking: p.allows_smoking,
          gender_preference: p.gender_preference || '',
        });
        setExistingImages(p.images || []);
      } catch (err) {
        setError('Failed to load property');
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const data = { ...form };
      ['price_per_month', 'price_per_semester', 'price_per_year', 'deposit_amount', 'latitude', 'longitude'].forEach(k => {
        data[k] = data[k] ? parseFloat(data[k]) : null;
      });
      data.total_rooms = parseInt(data.total_rooms);
      data.available_rooms = parseInt(data.available_rooms);
      data.max_occupants_per_room = parseInt(data.max_occupants_per_room);
      await propertiesAPI.update(id, data);
      setSuccess('Property updated successfully!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
    setPreviewUrls(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
  };

  const removeNewFile = (index) => {
    URL.revokeObjectURL(previewUrls[index]);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    try {
      await propertiesAPI.uploadImages(id, selectedFiles);
      const res = await propertiesAPI.getById(id);
      setExistingImages(res.data.images || []);
      setSelectedFiles([]);
      setPreviewUrls([]);
      setSuccess('Images uploaded successfully!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await propertiesAPI.deleteImage(id, imageId);
      setExistingImages(prev => prev.filter(img => img.id !== imageId));
      setSuccess('Image deleted');
    } catch (err) {
      setError('Failed to delete image');
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  if (!form) return <Alert severity="error">Property not found</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard/properties')}>Back</Button>
        <Typography variant="h4" fontWeight={700}>Edit Property</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ borderRadius: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Details" />
          <Tab label="Photos" />
          <Tab label="Rooms" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tab === 0 && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12}><TextField fullWidth label="Title" value={form.title} onChange={(e) => updateForm('title', e.target.value)} /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Description" multiline rows={4} value={form.description} onChange={(e) => updateForm('description', e.target.value)} /></Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth><InputLabel>Property Type</InputLabel>
                    <Select value={form.property_type} label="Property Type" onChange={(e) => updateForm('property_type', e.target.value)}>
                      {['hostel','apartment','shared_room','single_room','self_contained','flat'].map(t => <MenuItem key={t} value={t}>{t.replace('_',' ')}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth><InputLabel>Location</InputLabel>
                    <Select value={form.location_type} label="Location" onChange={(e) => updateForm('location_type', e.target.value)}>
                      <MenuItem value="on_campus">On Campus</MenuItem>
                      <MenuItem value="off_campus">Off Campus</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}><TextField fullWidth label="Address" value={form.address} onChange={(e) => updateForm('address', e.target.value)} /></Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    options={regions} value={form.region || ''} freeSolo
                    onChange={(e, v) => { updateForm('region', v || ''); updateForm('city', ''); if (v) fetchCities(v); }}
                    onInputChange={(e, v) => updateForm('region', v)}
                    renderInput={(params) => <TextField {...params} fullWidth label="Region" placeholder="Search region..." />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    freeSolo options={cities} value={form.city || ''}
                    onInputChange={(e, v) => updateForm('city', v)}
                    renderInput={(params) => <TextField {...params} fullWidth label="City / Town" placeholder="Search city..." />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    freeSolo options={universities} value={form.nearby_university || ''}
                    onInputChange={(e, v) => updateForm('nearby_university', v)}
                    renderInput={(params) => <TextField {...params} fullWidth label="Nearby University" placeholder="Search institution..." />}
                  />
                </Grid>
                <Grid item xs={6}><TextField fullWidth label="Distance to Campus" value={form.distance_to_campus} onChange={(e) => updateForm('distance_to_campus', e.target.value)} /></Grid>
                <Grid item xs={12}><Divider><Typography variant="body2" color="text.secondary">GPS Coordinates</Typography></Divider></Grid>
                <Grid item xs={4}><TextField fullWidth label="Latitude" type="number" value={form.latitude} onChange={(e) => updateForm('latitude', e.target.value)} onKeyDown={decimalOnly} /></Grid>
                <Grid item xs={4}><TextField fullWidth label="Longitude" type="number" value={form.longitude} onChange={(e) => updateForm('longitude', e.target.value)} onKeyDown={decimalOnly} /></Grid>
                <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button variant="outlined" startIcon={<MyLocation />} fullWidth onClick={() => {
                    navigator.geolocation.getCurrentPosition(
                      (pos) => { updateForm('latitude', pos.coords.latitude.toFixed(6)); updateForm('longitude', pos.coords.longitude.toFixed(6)); },
                      () => setError('Could not get GPS location'),
                      { enableHighAccuracy: true }
                    );
                  }}>Get Location</Button>
                </Grid>
                <Grid item xs={12}><Divider><Typography variant="body2" color="text.secondary">Pricing</Typography></Divider></Grid>
                <Grid item xs={4}><TextField fullWidth label="Price/Month" type="number" value={form.price_per_month} onChange={(e) => updateForm('price_per_month', e.target.value)} onKeyDown={decimalOnly} /></Grid>
                <Grid item xs={4}><TextField fullWidth label="Price/Semester" type="number" value={form.price_per_semester} onChange={(e) => updateForm('price_per_semester', e.target.value)} onKeyDown={decimalOnly} /></Grid>
                <Grid item xs={4}><TextField fullWidth label="Price/Year" type="number" value={form.price_per_year} onChange={(e) => updateForm('price_per_year', e.target.value)} onKeyDown={decimalOnly} /></Grid>
                <Grid item xs={6}><TextField fullWidth label="Deposit" type="number" value={form.deposit_amount} onChange={(e) => updateForm('deposit_amount', e.target.value)} onKeyDown={decimalOnly} /></Grid>
                <Grid item xs={12}><Divider><Typography variant="body2" color="text.secondary">Rooms</Typography></Divider></Grid>
                <Grid item xs={3}><TextField fullWidth label="Total Rooms" type="number" value={form.total_rooms} onChange={(e) => updateForm('total_rooms', e.target.value)} onKeyDown={integerOnly} /></Grid>
                <Grid item xs={3}><TextField fullWidth label="Available" type="number" value={form.available_rooms} onChange={(e) => updateForm('available_rooms', e.target.value)} onKeyDown={integerOnly} /></Grid>
                <Grid item xs={3}>
                  <FormControl fullWidth><InputLabel>Room Type</InputLabel>
                    <Select value={form.room_type} label="Room Type" onChange={(e) => updateForm('room_type', e.target.value)}>
                      {['single','double','triple','quad'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={3}><TextField fullWidth label="Max Occupants" type="number" value={form.max_occupants_per_room} onChange={(e) => updateForm('max_occupants_per_room', e.target.value)} onKeyDown={integerOnly} /></Grid>
                <Grid item xs={12}><Divider><Typography variant="body2" color="text.secondary">Amenities</Typography></Divider></Grid>
                {['has_electricity','has_water','has_wifi','has_security','has_parking','has_laundry','has_kitchen','has_furnished','has_ac','has_generator','allows_pets','allows_smoking','allows_pairing'].map(key => (
                  <Grid item xs={6} sm={4} key={key}>
                    <FormControlLabel control={<Switch checked={form[key]} onChange={(e) => updateForm(key, e.target.checked)} />} label={key.replace('has_','').replace('allows_','Allow ').replace(/_/g,' ')} sx={{ textTransform: 'capitalize' }} />
                  </Grid>
                ))}
                <Grid item xs={12}><TextField fullWidth label="House Rules" multiline rows={2} value={form.rules} onChange={(e) => updateForm('rules', e.target.value)} /></Grid>
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </>
          )}

          {tab === 1 && (
            <>
              <Typography variant="h6" fontWeight={600} gutterBottom>Current Images ({existingImages.length})</Typography>
              {existingImages.length > 0 ? (
                <ImageList cols={4} gap={8} sx={{ mb: 3 }}>
                  {existingImages.map((img) => (
                    <ImageListItem key={img.id} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                      <img src={img.image_url} alt={img.caption || 'Property'} loading="lazy" style={{ height: 180, objectFit: 'cover' }} />
                      <ImageListItemBar
                        position="top"
                        actionIcon={
                          <IconButton sx={{ color: 'white' }} onClick={() => handleDeleteImage(img.id)}>
                            <Delete />
                          </IconButton>
                        }
                        sx={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 100%)' }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>No images uploaded yet.</Typography>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" fontWeight={600} gutterBottom>Upload New Images</Typography>
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} />
              <Button variant="outlined" startIcon={<AddPhotoAlternate />} onClick={() => fileInputRef.current?.click()} sx={{ mb: 2 }}>
                Select Images
              </Button>
              {previewUrls.length > 0 && (
                <>
                  <ImageList cols={4} gap={8} sx={{ mb: 2 }}>
                    {previewUrls.map((url, i) => (
                      <ImageListItem key={i} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <img src={url} alt={`New ${i + 1}`} style={{ height: 180, objectFit: 'cover' }} />
                        <ImageListItemBar
                          position="top"
                          actionIcon={<IconButton sx={{ color: 'white' }} onClick={() => removeNewFile(i)}><Delete /></IconButton>}
                          sx={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 100%)' }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                  <Button variant="contained" startIcon={<CloudUpload />} onClick={handleUpload} disabled={uploading}>
                    {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Image(s)`}
                  </Button>
                </>
              )}
            </>
          )}

          {tab === 2 && <RoomManager propertyId={id} setError={setError} setSuccess={setSuccess} />}
        </Box>
      </Paper>
    </Box>
  );
}

const ROOM_FEATURES = [
  { key: 'has_bed', label: 'Bed' }, { key: 'has_wardrobe', label: 'Wardrobe' },
  { key: 'has_desk', label: 'Desk' }, { key: 'has_chair', label: 'Chair' },
  { key: 'has_fan', label: 'Fan' }, { key: 'has_ac', label: 'AC' },
  { key: 'has_private_bathroom', label: 'Private Bathroom' }, { key: 'has_private_kitchen', label: 'Private Kitchen' },
  { key: 'has_balcony', label: 'Balcony' }, { key: 'has_wifi', label: 'WiFi' },
  { key: 'has_tv', label: 'TV' }, { key: 'has_fridge', label: 'Fridge' },
  { key: 'has_microwave', label: 'Microwave' }, { key: 'has_washing_machine', label: 'Washing Machine' },
  { key: 'has_iron', label: 'Iron' }, { key: 'has_water_heater', label: 'Water Heater' },
  { key: 'has_curtains', label: 'Curtains' }, { key: 'has_mirror', label: 'Mirror' },
  { key: 'has_shoe_rack', label: 'Shoe Rack' },
];
const ROOM_BILLS = [
  { key: 'bills_included', label: 'All Bills Included' }, { key: 'electricity_included', label: 'Electricity' },
  { key: 'water_included', label: 'Water' }, { key: 'wifi_included', label: 'WiFi' },
  { key: 'cleaning_included', label: 'Cleaning' },
];

const emptyRoom = {
  room_number: '', room_label: '', floor: '', room_type: 'single', max_occupants: 1,
  price_per_month: '', price_per_semester: '', price_per_year: '', size_sqm: '', description: '',
  ...Object.fromEntries(ROOM_FEATURES.map(f => [f.key, false])),
  ...Object.fromEntries(ROOM_BILLS.map(b => [b.key, false])),
};

function RoomManager({ propertyId, setError, setSuccess }) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [roomForm, setRoomForm] = useState({ ...emptyRoom });
  const [saving, setSaving] = useState(false);
  const roomFileRef = useRef(null);
  const [roomFiles, setRoomFiles] = useState([]);
  const [uploadingRoomId, setUploadingRoomId] = useState(null);
  const dialogFileRef = useRef(null);
  const [dialogFiles, setDialogFiles] = useState([]);
  const [dialogPreviews, setDialogPreviews] = useState([]);

  const fetchRooms = async () => {
    try {
      const res = await propertiesAPI.listRooms(propertyId);
      setRooms(res.data);
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { fetchRooms(); }, [propertyId]);

  const openCreate = () => { setEditingRoom(null); setRoomForm({ ...emptyRoom }); setDialogFiles([]); setDialogPreviews([]); setDialogOpen(true); };
  const openEdit = (room) => {
    setEditingRoom(room);
    const f = {};
    Object.keys(emptyRoom).forEach(k => { f[k] = room[k] != null ? room[k] : emptyRoom[k]; });
    setRoomForm(f);
    setDialogFiles([]);
    setDialogPreviews([]);
    setDialogOpen(true);
  };

  const handleDialogFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setDialogFiles(prev => [...prev, ...files]);
    const newPreviews = files.map(f => URL.createObjectURL(f));
    setDialogPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeDialogFile = (index) => {
    setDialogFiles(prev => prev.filter((_, i) => i !== index));
    setDialogPreviews(prev => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSaveRoom = async () => {
    setSaving(true);
    try {
      const data = { ...roomForm };
      ['price_per_month', 'price_per_semester', 'price_per_year', 'size_sqm'].forEach(k => {
        data[k] = data[k] ? parseFloat(data[k]) : null;
      });
      data.max_occupants = parseInt(data.max_occupants) || 1;
      data.floor = data.floor ? parseInt(data.floor) : null;

      let roomId;
      if (editingRoom) {
        await propertiesAPI.updateRoom(propertyId, editingRoom.id, data);
        roomId = editingRoom.id;
        setSuccess('Room updated');
      } else {
        const res = await propertiesAPI.createRoom(propertyId, data);
        roomId = res.data?.id;
        setSuccess('Room created');
      }
      if (dialogFiles.length > 0 && roomId) {
        try {
          await propertiesAPI.uploadRoomImages(propertyId, roomId, dialogFiles);
          setSuccess(editingRoom ? 'Room updated with images' : 'Room created with images');
        } catch (_) {
          setError('Room saved but image upload failed. You can upload images later.');
        }
      }
      setDialogFiles([]); setDialogPreviews([]);
      setDialogOpen(false);
      fetchRooms();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save room');
    }
    setSaving(false);
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Delete this room and all its images?')) return;
    try {
      await propertiesAPI.deleteRoom(propertyId, roomId);
      setRooms(prev => prev.filter(r => r.id !== roomId));
      setSuccess('Room deleted');
    } catch (err) { setError('Failed to delete room'); }
  };

  const handleRoomImageUpload = async (roomId) => {
    if (roomFiles.length === 0) return;
    setUploadingRoomId(roomId);
    try {
      await propertiesAPI.uploadRoomImages(propertyId, roomId, roomFiles);
      setRoomFiles([]);
      setSuccess('Room images uploaded');
      fetchRooms();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to upload'); }
    setUploadingRoomId(null);
  };

  const handleDeleteRoomImage = async (roomId, imageId) => {
    try {
      await propertiesAPI.deleteRoomImage(propertyId, roomId, imageId);
      fetchRooms();
      setSuccess('Image deleted');
    } catch (_) { setError('Failed to delete image'); }
  };

  const updateRoomForm = (key, value) => setRoomForm(prev => ({ ...prev, [key]: value }));

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}><MeetingRoom sx={{ verticalAlign: 'middle', mr: 1 }} />Rooms ({rooms.length})</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Room</Button>
      </Box>

      {rooms.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No rooms added yet. Click "Add Room" to create individual rooms with features.</Typography>
      ) : (
        rooms.map((room) => {
          const activeFeatures = ROOM_FEATURES.filter(f => room[f.key]);
          const activeBills = ROOM_BILLS.filter(b => room[b.key]);
          const statusColor = room.status === 'available' ? 'success' : room.status === 'occupied' ? 'error' : 'warning';
          return (
            <Accordion key={room.id} sx={{ mb: 1, '&:before': { display: 'none' } }} variant="outlined">
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', flexWrap: 'wrap' }}>
                  <Typography fontWeight={600}>{room.room_label || `Room ${room.room_number}`}</Typography>
                  <Chip label={room.status} size="small" color={statusColor} sx={{ textTransform: 'capitalize' }} />
                  <Chip label={`${room.room_type}`} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                  {room.price_per_month && <Typography variant="body2" color="primary" fontWeight={600} sx={{ ml: 'auto', mr: 2 }}>GHS {room.price_per_month.toLocaleString()}/mo</Typography>}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1.5}>
                  {activeFeatures.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>Features</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {activeFeatures.map(f => <Chip key={f.key} icon={<CheckCircle sx={{ fontSize: 14 }} />} label={f.label} size="small" color="success" variant="outlined" />)}
                      </Box>
                    </Grid>
                  )}
                  {activeBills.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>Bills Included</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {activeBills.map(b => <Chip key={b.key} label={b.label} size="small" color="primary" variant="outlined" />)}
                      </Box>
                    </Grid>
                  )}
                  {room.images?.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>Photos ({room.images.length})</Typography>
                      <ImageList cols={5} rowHeight={100} gap={6}>
                        {room.images.map(img => (
                          <ImageListItem key={img.id}>
                            <img src={img.image_url} alt="" style={{ height: 100, objectFit: 'cover', borderRadius: 4 }} />
                            <ImageListItemBar position="top" actionIcon={
                              <IconButton size="small" sx={{ color: 'white' }} onClick={() => handleDeleteRoomImage(room.id, img.id)}><Delete fontSize="small" /></IconButton>
                            } sx={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 100%)' }} />
                          </ImageListItem>
                        ))}
                      </ImageList>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <input type="file" accept="image/*" multiple ref={roomFileRef} style={{ display: 'none' }}
                        onChange={(e) => setRoomFiles(Array.from(e.target.files))} />
                      <Button size="small" variant="outlined" startIcon={<AddPhotoAlternate />}
                        onClick={() => { roomFileRef.current?.click(); }}>
                        Select Photos
                      </Button>
                      {roomFiles.length > 0 && (
                        <Button size="small" variant="contained" startIcon={<CloudUpload />}
                          disabled={uploadingRoomId === room.id}
                          onClick={() => handleRoomImageUpload(room.id)}>
                          {uploadingRoomId === room.id ? 'Uploading...' : `Upload ${roomFiles.length}`}
                        </Button>
                      )}
                      <Box sx={{ flex: 1 }} />
                      <Button size="small" variant="outlined" onClick={() => openEdit(room)}>Edit</Button>
                      <Button size="small" variant="outlined" color="error" startIcon={<Delete />} onClick={() => handleDeleteRoom(room.id)}>Delete</Button>
                    </Stack>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingRoom ? 'Edit Room' : 'Add New Room'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6} sm={3}><TextField fullWidth label="Room Number" value={roomForm.room_number} onChange={(e) => updateRoomForm('room_number', e.target.value)} required /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth label="Room Label" value={roomForm.room_label} onChange={(e) => updateRoomForm('room_label', e.target.value)} placeholder="e.g. Suite A" /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth label="Floor" type="number" value={roomForm.floor} onChange={(e) => updateRoomForm('floor', e.target.value)} onKeyDown={integerOnly} /></Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth><InputLabel>Type</InputLabel>
                <Select value={roomForm.room_type} label="Type" onChange={(e) => updateRoomForm('room_type', e.target.value)}>
                  {['single','double','triple','quad'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth label="Max Occupants" type="number" value={roomForm.max_occupants} onChange={(e) => updateRoomForm('max_occupants', e.target.value)} onKeyDown={integerOnly} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth label="Size (sqm)" type="number" value={roomForm.size_sqm} onChange={(e) => updateRoomForm('size_sqm', e.target.value)} onKeyDown={decimalOnly} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth label="Price/Month" type="number" value={roomForm.price_per_month} onChange={(e) => updateRoomForm('price_per_month', e.target.value)} onKeyDown={decimalOnly} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth label="Price/Semester" type="number" value={roomForm.price_per_semester} onChange={(e) => updateRoomForm('price_per_semester', e.target.value)} onKeyDown={decimalOnly} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Description" multiline rows={2} value={roomForm.description} onChange={(e) => updateRoomForm('description', e.target.value)} /></Grid>
            <Grid item xs={12}><Divider><Typography variant="body2" color="text.secondary">Features & Accessories</Typography></Divider></Grid>
            {ROOM_FEATURES.map(f => (
              <Grid item xs={6} sm={4} md={3} key={f.key}>
                <FormControlLabel control={<Checkbox size="small" checked={roomForm[f.key]} onChange={(e) => updateRoomForm(f.key, e.target.checked)} />} label={<Typography variant="body2">{f.label}</Typography>} />
              </Grid>
            ))}
            <Grid item xs={12}><Divider><Typography variant="body2" color="text.secondary">Bills & Utilities Included</Typography></Divider></Grid>
            {ROOM_BILLS.map(b => (
              <Grid item xs={6} sm={4} key={b.key}>
                <FormControlLabel control={<Checkbox size="small" checked={roomForm[b.key]} onChange={(e) => updateRoomForm(b.key, e.target.checked)} />} label={<Typography variant="body2">{b.label}</Typography>} />
              </Grid>
            ))}
            <Grid item xs={12}><Divider><Typography variant="body2" color="text.secondary">Room Photos</Typography></Divider></Grid>
            <Grid item xs={12}>
              <input type="file" accept="image/*" multiple ref={dialogFileRef} style={{ display: 'none' }} onChange={handleDialogFileSelect} />
              <Button size="small" variant="outlined" startIcon={<AddPhotoAlternate />} onClick={() => dialogFileRef.current?.click()}>
                Add Photos {dialogFiles.length > 0 && `(${dialogFiles.length} selected)`}
              </Button>
              {dialogPreviews.length > 0 && (
                <ImageList cols={6} rowHeight={80} gap={6} sx={{ mt: 1 }}>
                  {dialogPreviews.map((preview, idx) => (
                    <ImageListItem key={idx}>
                      <img src={preview} alt="" style={{ height: 80, objectFit: 'cover', borderRadius: 4 }} />
                      <ImageListItemBar position="top" actionIcon={
                        <IconButton size="small" sx={{ color: 'white' }} onClick={() => removeDialogFile(idx)}><Delete fontSize="small" /></IconButton>
                      } sx={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 100%)' }} />
                    </ImageListItem>
                  ))}
                </ImageList>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRoom} disabled={saving || !roomForm.room_number}>
            {saving ? 'Saving...' : editingRoom ? 'Update Room' : 'Create Room'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default EditPropertyPage;
